/**
 * SVG renderer for freeform text-label drawings. Follows the same
 * LineRenderCtx pattern as lineRenderers.tsx — interactive (select /
 * drag / delete), renders into the chart's SVG overlay.
 */
import type { ReactNode, MouseEvent } from "react";
import type { AppDispatch } from "@/state/store";
import {
  removeTextLabel,
  setSelectedDrawing,
  type SelectedDrawing,
  type TextLabel,
} from "@/state/slices/uiSlice";
import type { AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

export interface TextRenderCtx {
  series: AnySeries | null;
  timeToX: (time: number) => number | null;
  selectedDrawing: SelectedDrawing | null;
  tabId: string;
  /** Id of the label currently being inline-edited (rendered as an HTML
   *  input overlay in Chart.tsx, so we skip its SVG copy). */
  editingId: string | null;
  /** Open the inline editor for a label (double-click). */
  onEdit: (label: TextLabel) => void;
  /** Effective default colour for labels with no explicit colour: the
   *  text-tool setting if set, else the chart chrome text colour (so
   *  labels stay readable when the theme flips light/dark). */
  textColor: string;
  /** Effective default font size / bold from the text-tool settings,
   *  applied to labels without a per-instance override. */
  defaultFontSize: number;
  defaultBold: boolean;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  /** Locked drawings pan the chart instead of moving — same as line/fib. */
  startLockedPan: (nativeEvent: globalThis.MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  dispatch: AppDispatch;
}

export function renderTextLabel(
  label: TextLabel,
  ctx: TextRenderCtx,
): ReactNode {
  const { series } = ctx;
  if (!series) return null;
  // While this label is being inline-edited, the HTML input overlay in
  // Chart.tsx stands in for it — skip the SVG copy so they don't double up.
  if (ctx.editingId === label.id) return null;
  const x = ctx.timeToX(label.time);
  if (x == null) return null;
  const y = series.priceToCoordinate(label.price);
  if (y == null) return null;

  if (label.visible === false) return null;

  const isSelected =
    ctx.selectedDrawing?.kind === "text" && ctx.selectedDrawing.id === label.id;
  const color = label.color ?? ctx.textColor;
  const fontSize = label.fontSize ?? ctx.defaultFontSize;
  const fontWeight = (label.bold ?? ctx.defaultBold) ? "bold" : "normal";
  const displayText = label.text || "Text";

  return (
    <g key={label.id}>
      {/* Invisible hit-box larger than the text for easier selection */}
      <rect
        x={x - 4}
        y={y - fontSize - 4}
        width={displayText.length * fontSize * 0.6 + 8}
        height={fontSize + 10}
        fill="transparent"
        className={styles.textHit}
        data-drawing-hit="true"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          ctx.dispatch(
            setSelectedDrawing({
              tabId: ctx.tabId,
              selected: { kind: "text", id: label.id },
            }),
          );
          // Locked → pan the chart instead of moving the label.
          if (label.locked) {
            ctx.startLockedPan(e.nativeEvent);
          } else {
            ctx.startDrag({ type: "text", id: label.id })(e);
          }
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          ctx.onEdit(label);
        }}
        onContextMenu={ctx.showMenu([
          {
            label: "Edit text",
            action: () => ctx.onEdit(label),
          },
          {
            label: "Delete",
            action: () =>
              ctx.dispatch(removeTextLabel({ tabId: ctx.tabId, id: label.id })),
            danger: true,
          },
        ])}
      />
      {/* Selection highlight */}
      {isSelected && (
        <rect
          x={x - 4}
          y={y - fontSize - 4}
          width={displayText.length * fontSize * 0.6 + 8}
          height={fontSize + 10}
          fill="rgba(61,123,255,0.12)"
          stroke="#3D7BFF"
          strokeWidth={1}
          rx={3}
          pointerEvents="none"
        />
      )}
      <text
        x={x}
        y={y}
        fill={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily="Archivo, sans-serif"
        pointerEvents="none"
        style={{ userSelect: "none" }}
      >
        {displayText}
      </text>
    </g>
  );
}
